'use client';

import { useRef } from 'react';
import { appAssetsCdnUrl } from '~/constants/environment';
import ConditionalTile from '~/constants/srp/ConditionalTile';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { NewCarVariant } from '~/utilities/srp/new-car/getNewCarVariant';
import imageStyles from './modelImage.module.css';
import { getModelAssetSrc } from './newCarModels';
import NewCarModelSlider from './NewCarModelSlider';
import { ContentContainer, StyledLink, TileCta } from '../NewCarTile.styles';
import ObservableTile from '../ObservableTile';

type OwnProps = {
  href: string;
  make: string;
  model?: string;
  position: number;
  variant: NewCarVariant.Make | NewCarVariant.MakeModel;
};

export default function MakeModelNewCarTile({
  position,
  href,
  variant,
  make,
  model
}: OwnProps) {
  const trackEvent = useTrackEvent();
  const observeRef = useRef(null);

  const isMakeModel = variant === NewCarVariant.MakeModel;

  return (
    <ObservableTile
      observeRef={observeRef}
      name={ConditionalTile.NewCarConfigurator}
      onVisible={() =>
        trackEvent('Conditional tile', {
          action: 'viewed',
          position,
          tile: ConditionalTile.NewCarConfigurator,
          variant
        })
      }
    >
      <StyledLink
        id={ConditionalTile.NewCarConfigurator}
        data-testid="conditional-tile-link"
        data-adobe-id="new-car-tile-select-color-options-more"
        data-analytics-id="Conditional Tile"
        data-analytics-attributes={JSON.stringify({
          position,
          tile: ConditionalTile.NewCarConfigurator,
          variant
        })}
        href={href}
        className="bg-cover bg-center"
        style={{
          backgroundImage: `url("${appAssetsCdnUrl}/images/new-car/make-model-tile-background.svg")`
        }}
      >
        <ContentContainer>
          <div className="flex flex-col gap-lg">
            <h3 className="t-heading-xl text-text-inverse">
              {isMakeModel ? (
                <>
                  Build your perfect new{' '}
                  <span className="text-brand-tertiary capitalize">
                    {make} {model}
                  </span>
                </>
              ) : (
                <>
                  Build your perfect new{' '}
                  <span className="text-brand-tertiary capitalize">{make}</span>
                </>
              )}
            </h3>
            <p className="t-body-sm text-text-inverse">
              All brand new models — pickup or delivery as soon as today!
            </p>
          </div>

          <div
            aria-hidden="true"
            className="relative w-full flex-1 min-h-[140px]"
          >
            {isMakeModel ? (
              <img
                alt=""
                className={imageStyles.modelImage}
                src={getModelAssetSrc(make, model ?? '')}
              />
            ) : (
              <NewCarModelSlider make={make} />
            )}
          </div>

          <TileCta aria-hidden="true" inverse tabIndex={-1}>
            Select Color, Options & More
          </TileCta>
        </ContentContainer>
      </StyledLink>
    </ObservableTile>
  );
}
